import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Building2, 
  Users, 
  FileText, 
  Scale, 
  Workflow, 
  CheckCircle2, 
  AlertCircle,
  Lightbulb,
  Target,
  Shield,
  Brain,
  TrendingUp,
  Briefcase,
  ChevronRight,
  Info,
  Plus,
  X,
  Save,
  Sparkles,
  Loader2
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { OrganizationFilter, useOrganizationFilter } from '@/components/admin/OrganizationFilter';
import { CoEStrategicConfig } from '@/components/admin/CoEStrategicConfig';
import { useCoEConfig, useCoEPolicies, useCoERegulatoryCompliance, useCoEProcesses, useCoERoleAssignments, useCoEFrameworkSelection, useCoERoleDefinitions, type CoERoleDefinition } from '@/hooks/useCoEConfig';
import { CoERoleDialog } from '@/components/admin/CoERoleDialog';
import { Pencil, Trash2 } from 'lucide-react';
import { Settings2 } from "lucide-react";

// Mock data for CoE
const operatingModels = [
  {
    id: "centralized",
    name: "Centralizado",
    description: "Un único equipo central controla todas las iniciativas de IA",
    pros: ["Mayor control y consistencia", "Economías de escala", "Estándares unificados", "Gobierno más simple"],
    cons: ["Puede ser lento para responder", "Cuello de botella potencial", "Menor autonomía de unidades"],
    recommended: false,
    bestFor: "Empresas pequeñas-medianas o en etapas iniciales de adopción de IA"
  },
  {
    id: "decentralized",
    name: "Descentralizado",
    description: "Cada unidad de negocio tiene su propio equipo de IA",
    pros: ["Mayor agilidad", "Proximidad al negocio", "Innovación distribuida"],
    cons: ["Duplicación de esfuerzos", "Inconsistencia en estándares", "Dificultad de gobierno"],
    recommended: false,
    bestFor: "Conglomerados con unidades de negocio muy diferentes"
  },
  {
    id: "hybrid",
    name: "Híbrido (Hub & Spoke)",
    description: "Centro de excelencia central + equipos embebidos en unidades de negocio",
    pros: ["Balance entre control y agilidad", "Estándares compartidos", "Innovación local", "Escalabilidad"],
    cons: ["Requiere coordinación efectiva", "Complejidad organizacional"],
    recommended: true,
    bestFor: "La mayoría de empresas medianas-grandes"
  }
];

// keyRoles legacy hardcoded list moved to coe_role_definitions table (DEFAULT_ROLE_DEFINITIONS in useCoEConfig).

// Renombrado para evitar shadowing con el hook useCoEPolicies
const policyTemplatesByCategory = [
  {
    category: "Transparencia",
    icon: FileText,
  },
  {
    category: "Accountability",
    icon: Scale,
  },
  {
    category: "Equidad",
    icon: Users,
  }
];

const regulatoryFrameworks = [
  {
    name: "EU AI Act",
    region: "Unión Europea",
    status: "Vigente",
    keyRequirements: [
      "Clasificación de sistemas por nivel de riesgo (inaceptable, alto, limitado, mínimo)",
      "Requisitos estrictos para sistemas de alto riesgo",
      "Prohibición de ciertas prácticas (social scoring, manipulación subliminal)",
      "Obligaciones de transparencia para chatbots y deepfakes",
      "Multas hasta €35M o 7% de facturación global"
    ],
    recommendations: [
      "Realizar inventario de sistemas de IA y clasificarlos por riesgo",
      "Implementar evaluaciones de conformidad para sistemas de alto riesgo",
      "Establecer procesos de documentación técnica completa",
      "Designar un responsable de cumplimiento de IA"
    ]
  },
  {
    name: "GDPR",
    region: "Unión Europea",
    status: "Vigente",
    keyRequirements: [
      "Base legal para procesamiento de datos personales",
      "Derecho a no ser sujeto de decisiones automatizadas (Art. 22)",
      "Derecho de acceso y explicación",
      "Evaluaciones de impacto en protección de datos (DPIA)",
      "Privacy by design y by default"
    ],
    recommendations: [
      "Realizar DPIAs antes de desplegar sistemas de IA con datos personales",
      "Implementar mecanismos de opt-out de decisiones automatizadas",
      "Mantener registros de actividades de tratamiento",
      "Entrenar equipos en privacidad y protección de datos"
    ]
  },
  {
    name: "ISO/IEC 42001",
    region: "Internacional",
    status: "Vigente",
    keyRequirements: [
      "Sistema de gestión de IA certificable",
      "Requisitos de gobernanza y responsabilidad",
      "Gestión de riesgos específica para IA",
      "Mejora continua de prácticas de IA"
    ],
    recommendations: [
      "Considerar certificación para demostrar madurez",
      "Alinear políticas internas con el estándar",
      "Establecer métricas de gestión de IA",
      "Realizar auditorías internas periódicas"
    ]
  }
];


export default function CoEManagementPage() {
  const { profile, currentRole } = useAuth();
  const isSuperAdmin = currentRole === 'super_admin';
  const [selectedOrg, setSelectedOrg] = useState<string>(
    isSuperAdmin ? 'all' : (profile?.organization_id || '')
  );
  const { showAllOrgs, getOrgName } = useOrganizationFilter(selectedOrg);

  // Hooks de persistencia (solo se activan si hay org seleccionada)
  const { config, saveConfig } = useCoEConfig(selectedOrg !== 'all' ? selectedOrg : '');
  const { policies, loading: policiesLoading, toggleStatus, updateNotes: updatePolicyNotes, addPolicy, deletePolicy } = useCoEPolicies(selectedOrg !== 'all' ? selectedOrg : '');
  const { compliance, loading: complianceLoading, toggleCompliance, updateNotes: updateComplianceNotes, addRequirement, deleteRequirement, frameworks } = useCoERegulatoryCompliance(selectedOrg !== 'all' ? selectedOrg : '');
  const { selections: frameworkSelections, toggleFramework, isFrameworkActive } = useCoEFrameworkSelection(selectedOrg !== 'all' ? selectedOrg : '');
  const { processes, loading: processesLoading, addProcess, updateMaturity, deleteProcess } = useCoEProcesses(selectedOrg !== 'all' ? selectedOrg : '');
  const { assignments, upsertAssignment } = useCoERoleAssignments(selectedOrg !== 'all' ? selectedOrg : '');
  const { roles: roleDefinitions, addRole, updateRole, deleteRole } = useCoERoleDefinitions(selectedOrg !== 'all' ? selectedOrg : '');
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<CoERoleDefinition | null>(null);

  // Icon resolver: map string -> lucide component
  const roleIconMap: Record<string, any> = { Brain, Target, Shield, Users, Lightbulb, Briefcase, TrendingUp, Workflow, Building2, FileText, Scale };
  const getRoleIcon = (name: string | null) => roleIconMap[name || 'Users'] || Users;

  // Estados para recomendador IA de requisitos regulatorios
  interface RecommendedRequirement {
    framework_name: string;
    label: string;
    description: string;
    priority: string;
    justification: string;
    reference: string;
    selected: boolean;
  }
  const [isRecommendingReqs, setIsRecommendingReqs] = useState(false);
  const [isRecommendReqsDialogOpen, setIsRecommendReqsDialogOpen] = useState(false);
  const [recommendedRequirements, setRecommendedRequirements] = useState<RecommendedRequirement[]>([]);

  // Estados para agregar requisito manual
  const [isAddReqOpen, setIsAddReqOpen] = useState(false);
  const [newReqFramework, setNewReqFramework] = useState('EU AI Act');
  const [newReqLabel, setNewReqLabel] = useState('');
  const [newReqDescription, setNewReqDescription] = useState('');
  const [newReqPriority, setNewReqPriority] = useState('Media');

  const [selectedModel, setSelectedModel] = useState<string>("hybrid");
  const [newProcessName, setNewProcessName] = useState("");
  const [newProcessDesc, setNewProcessDesc] = useState("");

  // Estados para recomendador IA de procesos
  interface RecommendedProcess {
    name: string;
    description: string;
    category: string;
    priority: string;
    reference: string;
    justification: string;
    selected: boolean;
  }
  const [isRecommendingProcesses, setIsRecommendingProcesses] = useState(false);
  const [isRecommendProcessDialogOpen, setIsRecommendProcessDialogOpen] = useState(false);
  const [recommendedProcessesList, setRecommendedProcessesList] = useState<RecommendedProcess[]>([]);
  
  // Estados para agregar política
  const [isAddPolicyOpen, setIsAddPolicyOpen] = useState(false);
  const [newPolicyCategory, setNewPolicyCategory] = useState<string>("Transparencia");
  const [newPolicyName, setNewPolicyName] = useState("");
  const [newPolicyDescription, setNewPolicyDescription] = useState("");
  const [newPolicyPriority, setNewPolicyPriority] = useState<string>("Media");

  // Estados para recomendador IA
  interface RecommendedPolicy {
    name: string;
    category: string;
    description: string;
    priority: string;
    justification: string;
    iso_reference: string;
    selected: boolean;
  }
  const [isRecommending, setIsRecommending] = useState(false);
  const [isRecommendDialogOpen, setIsRecommendDialogOpen] = useState(false);
  const [recommendedPolicies, setRecommendedPolicies] = useState<RecommendedPolicy[]>([]);

  // Sincronizar modelo operativo con BD
  useEffect(() => {
    if (config?.operating_model) {
      setSelectedModel(config.operating_model);
    }
  }, [config]);

  const handleSaveOperatingModel = () => {
    if (selectedOrg && selectedOrg !== 'all') {
      saveConfig({ operating_model: selectedModel });
    }
  };

  const handleAddPolicy = () => {
    if (newPolicyName.trim() && selectedOrg && selectedOrg !== 'all') {
      addPolicy({
        category: newPolicyCategory,
        name: newPolicyName.trim(),
        description: newPolicyDescription.trim() || null,
        priority: newPolicyPriority,
        status: 'pending',
        notes: null,
        is_default: false,
        sort_order: policies.length,
      });
      setNewPolicyName('');
      setNewPolicyDescription('');
      setNewPolicyPriority('Media');
      setIsAddPolicyOpen(false);
    }
  };

  const handleRecommendPolicies = async () => {
    if (!selectedOrg || selectedOrg === 'all') return;
    setIsRecommending(true);
    try {
      const { data, error } = await supabase.functions.invoke('recommend-coe-policies', {
        body: { organization_id: selectedOrg },
      });
      if (error) throw error;
      if (data?.policies) {
        setRecommendedPolicies(data.policies.map((p: any) => ({ ...p, selected: true })));
        setIsRecommendDialogOpen(true);
      } else {
        toast({ title: 'Sin recomendaciones', description: 'La IA no generó recomendaciones.', variant: 'destructive' });
      }
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'No se pudieron obtener recomendaciones.', variant: 'destructive' });
    } finally {
      setIsRecommending(false);
    }
  };

  const handleAddSelectedPolicies = () => {
    const selected = recommendedPolicies.filter(p => p.selected);
    selected.forEach(p => {
      addPolicy({
        category: p.category,
        name: p.name,
        description: p.description,
        priority: p.priority,
        status: 'pending',
        notes: `ISO 42001: ${p.iso_reference}\nJustificación: ${p.justification}`,
        is_default: false,
        sort_order: policies.length,
      });
    });
    setIsRecommendDialogOpen(false);
    setRecommendedPolicies([]);
    toast({ title: `${selected.length} políticas agregadas`, description: 'Las políticas recomendadas se han agregado exitosamente.' });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Building2 className="h-8 w-8 text-primary" />
            Centro de Excelencia en IA
          </h1>
          <p className="text-muted-foreground mt-1">
            {isSuperAdmin && showAllOrgs 
              ? 'Configuración para todas las organizaciones' 
              : `Configuración de ${getOrgName()}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <OrganizationFilter selectedOrg={selectedOrg} onOrgChange={setSelectedOrg} />
        </div>
      </div>

      {/* Dashboard Resumen */}
      {selectedOrg && selectedOrg !== 'all' && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {(() => {
            const assignedRoles = assignments.filter(a => a.status === 'assigned').length;
            const implementedPolicies = policies.filter(p => p.status === 'implemented').length;
            const compliantReqs = compliance.filter(c => c.is_compliant).length;
            const totalReqs = compliance.length;
            const avgMaturity = processes.length > 0
              ? (processes.reduce((sum, p) => sum + (p.maturity_level || 0), 0) / processes.length).toFixed(1)
              : '0';
            const visionLabel = config?.operating_model
              ? operatingModels.find(m => m.id === config.operating_model)?.name || '—'
              : '—';

            return (
              <>
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Modelo Operativo</p>
                  <p className="text-sm font-bold mt-1">{visionLabel}</p>
                </Card>
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Roles Cubiertos</p>
                  <p className="text-sm font-bold mt-1">{assignedRoles}/{roleDefinitions.length || 0}</p>
                  <Progress value={roleDefinitions.length ? (assignedRoles / roleDefinitions.length) * 100 : 0} className="h-1.5 mt-1" />
                </Card>
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Políticas Implementadas</p>
                  <p className="text-sm font-bold mt-1">{implementedPolicies}/{policies.length}</p>
                  <Progress value={policies.length ? (implementedPolicies / policies.length) * 100 : 0} className="h-1.5 mt-1" />
                </Card>
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Cumplimiento Regulatorio</p>
                  <p className="text-sm font-bold mt-1">{compliantReqs}/{totalReqs}</p>
                  <Progress value={totalReqs ? (compliantReqs / totalReqs) * 100 : 0} className="h-1.5 mt-1" />
                </Card>
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Madurez Promedio</p>
                  <p className="text-sm font-bold mt-1">{avgMaturity}/5</p>
                  <Progress value={(parseFloat(avgMaturity) / 5) * 100} className="h-1.5 mt-1" />
                </Card>
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Procesos</p>
                  <p className="text-sm font-bold mt-1">{processes.length}</p>
                </Card>
              </>
            );
          })()}
        </div>
      )}

      <Tabs defaultValue="strategic-config" className="space-y-6">
        <TabsList className="grid w-full grid-cols-6 h-auto p-1">
          <TabsTrigger value="strategic-config" className="flex flex-col py-3 gap-1">
            <Settings2 className="h-4 w-4" />
            <span className="text-xs">Configuración</span>
          </TabsTrigger>
          <TabsTrigger value="operating-model" className="flex flex-col py-3 gap-1">
            <Building2 className="h-4 w-4" />
            <span className="text-xs">Modelo Operativo</span>
          </TabsTrigger>
          <TabsTrigger value="roles" className="flex flex-col py-3 gap-1">
            <Users className="h-4 w-4" />
            <span className="text-xs">Roles Clave</span>
          </TabsTrigger>
          <TabsTrigger value="policies" className="flex flex-col py-3 gap-1">
            <FileText className="h-4 w-4" />
            <span className="text-xs">Políticas</span>
          </TabsTrigger>
          <TabsTrigger value="regulatory" className="flex flex-col py-3 gap-1">
            <Scale className="h-4 w-4" />
            <span className="text-xs">Marco Regulatorio</span>
          </TabsTrigger>
          <TabsTrigger value="processes" className="flex flex-col py-3 gap-1">
            <Workflow className="h-4 w-4" />
            <span className="text-xs">Procesos</span>
          </TabsTrigger>
        </TabsList>

        {/* Configuración Estratégica */}
        <TabsContent value="strategic-config">
          {selectedOrg && selectedOrg !== 'all' ? (
            <CoEStrategicConfig organizationId={selectedOrg} />
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Selecciona una organización específica para configurar el Centro de Excelencia
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Modelo Operativo */}
        <TabsContent value="operating-model" className="space-y-6">
          {selectedOrg && selectedOrg !== 'all' ? (
            <>
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-lg font-semibold">Modelo Operativo</h2>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <p>Define la estructura organizacional del CoE: centralizado, descentralizado o híbrido. Elige el modelo que mejor se adapte al tamaño y madurez de tu empresa.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Card>
                <CardHeader>
                  <CardTitle>Selecciona el Modelo Operativo</CardTitle>
                  <CardDescription>
                    Define la estructura organizacional más adecuada para tu CoE de IA
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    {operatingModels.map((model) => (
                      <Card 
                        key={model.id}
                        className={`cursor-pointer transition-all hover:shadow-md ${
                          selectedModel === model.id 
                            ? 'ring-2 ring-primary border-primary' 
                            : ''
                        }`}
                        onClick={() => setSelectedModel(model.id)}
                      >
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg">{model.name}</CardTitle>
                            {model.recommended && (
                              <Badge className="bg-green-500">Recomendado</Badge>
                            )}
                          </div>
                          <CardDescription className="text-sm">
                            {model.description}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <p className="text-xs font-semibold text-green-600 mb-1">Ventajas:</p>
                            <ul className="text-xs space-y-1">
                              {model.pros.map((pro, i) => (
                                <li key={i} className="flex items-start gap-1">
                                  <CheckCircle2 className="h-3 w-3 text-green-500 mt-0.5 shrink-0" />
                                  {pro}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-red-600 mb-1">Desventajas:</p>
                            <ul className="text-xs space-y-1">
                              {model.cons.map((con, i) => (
                                <li key={i} className="flex items-start gap-1">
                                  <AlertCircle className="h-3 w-3 text-red-500 mt-0.5 shrink-0" />
                                  {con}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="pt-2 border-t">
                            <p className="text-xs text-muted-foreground">
                              <strong>Mejor para:</strong> {model.bestFor}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={handleSaveOperatingModel} className="gap-2">
                      <Save className="h-4 w-4" /> Guardar Modelo Operativo
                    </Button>
                  </div>
                </CardContent>
              </Card>


              {selectedModel && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lightbulb className="h-5 w-5 text-yellow-500" />
                      Recomendaciones para Modelo {operatingModels.find(m => m.id === selectedModel)?.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold mb-3">Áreas que necesitas:</h4>
                        <div className="space-y-2">
                          {selectedModel === "centralized" && (
                            <>
                              <Badge variant="outline" className="mr-2">Desarrollo de IA</Badge>
                              <Badge variant="outline" className="mr-2">Datos & MLOps</Badge>
                              <Badge variant="outline" className="mr-2">Gobernanza</Badge>
                              <Badge variant="outline" className="mr-2">Capacitación</Badge>
                            </>
                          )}
                          {selectedModel === "decentralized" && (
                            <>
                              <Badge variant="outline" className="mr-2">Equipo IA por BU</Badge>
                              <Badge variant="outline" className="mr-2">Coordinación Central (mínima)</Badge>
                              <Badge variant="outline" className="mr-2">Estándares Compartidos</Badge>
                            </>
                          )}
                          {selectedModel === "hybrid" && (
                            <>
                              <Badge variant="outline" className="mr-2">Hub Central de Excelencia</Badge>
                              <Badge variant="outline" className="mr-2">Plataforma & MLOps</Badge>
                              <Badge variant="outline" className="mr-2">Gobernanza & Compliance</Badge>
                              <Badge variant="outline" className="mr-2">Centro de Capacitación</Badge>
                              <Badge variant="outline" className="mr-2">Equipos Embebidos (Spokes)</Badge>
                            </>
                          )}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-3">Tamaño estimado del equipo:</h4>
                        <div className="space-y-2 text-sm">
                          {selectedModel === "centralized" && (
                            <p>5-15 personas dependiendo del volumen de iniciativas</p>
                          )}
                          {selectedModel === "decentralized" && (
                            <p>2-5 personas por unidad de negocio + 2-3 de coordinación</p>
                          )}
                          {selectedModel === "hybrid" && (
                            <p>8-20 en el hub central + 1-3 por cada unidad de negocio</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Selecciona una organización específica para gestionar el modelo operativo
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Roles Clave */}
        <TabsContent value="roles" className="space-y-6">
          {selectedOrg && selectedOrg !== 'all' ? (
            <>
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold">Roles Clave</h2>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <p>Asigna las personas responsables de cada rol clave del CoE. Puedes agregar, editar o eliminar roles según tu organización.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Button size="sm" onClick={() => { setEditingRole(null); setRoleDialogOpen(true); }} className="gap-2">
                <Plus className="h-4 w-4" /> Agregar rol
              </Button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roleDefinitions.map((role) => {
                const assignment = assignments.find(a => a.role_key === role.role_key);
                const roleStatus = assignment?.status || 'vacant';
                const RoleIcon = getRoleIcon(role.icon_name);
                return (
                  <Card key={role.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${role.color || 'bg-primary'}`}>
                          <RoleIcon className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-base">{role.title}</CardTitle>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {role.seniority && (
                              <Badge variant="secondary" className="text-xs">
                                {role.seniority}
                              </Badge>
                            )}
                            <Badge 
                              variant={roleStatus === 'assigned' ? 'default' : roleStatus === 'in_search' ? 'warning' : 'outline'}
                              className="text-xs"
                            >
                              {roleStatus === 'assigned' ? 'Asignado' : roleStatus === 'in_search' ? 'En búsqueda' : 'Vacante'}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingRole(role); setRoleDialogOpen(true); }}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => {
                            if (confirm(`¿Eliminar el rol "${role.title}"? Esto también eliminará su asignación.`)) {
                              deleteRole({ id: role.id!, role_key: role.role_key });
                            }
                          }}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Assignment fields */}
                      <div className="space-y-2 p-3 rounded-lg border bg-muted/30">
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-muted-foreground">Persona asignada</label>
                          <Input
                            placeholder="Nombre completo"
                            defaultValue={assignment?.assigned_name || ''}
                            onBlur={(e) => {
                              const val = e.target.value;
                              upsertAssignment({
                                organization_id: selectedOrg,
                                role_key: role.role_key,
                                assigned_name: val || null,
                                assigned_email: assignment?.assigned_email || null,
                                status: val ? 'assigned' : 'vacant',
                                notes: assignment?.notes || null,
                              });
                            }}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-muted-foreground">Email</label>
                          <Input
                            placeholder="email@empresa.com"
                            defaultValue={assignment?.assigned_email || ''}
                            onBlur={(e) => {
                              upsertAssignment({
                                organization_id: selectedOrg,
                                role_key: role.role_key,
                                assigned_name: assignment?.assigned_name || null,
                                assigned_email: e.target.value || null,
                                status: assignment?.status || 'vacant',
                                notes: assignment?.notes || null,
                              });
                            }}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-muted-foreground">Estado</label>
                          <Select
                            value={roleStatus}
                            onValueChange={(val) => {
                              upsertAssignment({
                                organization_id: selectedOrg,
                                role_key: role.role_key,
                                assigned_name: assignment?.assigned_name || null,
                                assigned_email: assignment?.assigned_email || null,
                                status: val,
                                notes: assignment?.notes || null,
                              });
                            }}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="vacant">Vacante</SelectItem>
                              <SelectItem value="in_search">En búsqueda</SelectItem>
                              <SelectItem value="assigned">Asignado</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-muted-foreground">Notas</label>
                          <Textarea
                            placeholder="Notas sobre este rol..."
                            defaultValue={assignment?.notes || ''}
                            onBlur={(e) => {
                              upsertAssignment({
                                organization_id: selectedOrg,
                                role_key: role.role_key,
                                assigned_name: assignment?.assigned_name || null,
                                assigned_email: assignment?.assigned_email || null,
                                status: assignment?.status || 'vacant',
                                notes: e.target.value || null,
                              });
                            }}
                            className="text-xs min-h-[50px]"
                          />
                        </div>
                      </div>

                      <Accordion type="single" collapsible>
                        <AccordionItem value="details" className="border-0">
                          <AccordionTrigger className="text-xs py-1 hover:no-underline">
                            Ver responsabilidades y habilidades
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-3">
                              <div>
                                <p className="text-xs font-semibold mb-2">Responsabilidades:</p>
                                <ul className="text-xs space-y-1">
                                  {role.responsibilities.map((resp, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                      <ChevronRight className="h-3 w-3 text-primary mt-0.5 shrink-0" />
                                      {resp}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <p className="text-xs font-semibold mb-2">Habilidades clave:</p>
                                <div className="flex flex-wrap gap-1">
                                  {role.skills.map((skill, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">
                                      {skill}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Selecciona una organización específica para gestionar roles
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Políticas */}
        <TabsContent value="policies" className="space-y-6">
          <div className="flex items-center gap-2 mb-2">
            <h2 className="text-lg font-semibold">Políticas de IA Responsable</h2>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p>Define las políticas éticas y de gobernanza de IA de tu organización (transparencia, accountability, equidad, privacidad). Marca el estado de implementación de cada una y agrega evidencias en las notas. Usa el botón "Recomendar con IA" para obtener sugerencias basadas en ISO 42001.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {selectedOrg && selectedOrg !== 'all' ? (
            policiesLoading ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  Cargando políticas...
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Políticas de IA Responsable</CardTitle>
                      <CardDescription>
                        Gestiona políticas que enfatizan transparencia, accountability y equidad
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRecommendPolicies}
                        disabled={isRecommending}
                      >
                        {isRecommending ? (
                          <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Analizando...</>
                        ) : (
                          <><Sparkles className="h-4 w-4 mr-1" /> Recomendar con IA</>
                        )}
                      </Button>
                      <Dialog open={isAddPolicyOpen} onOpenChange={setIsAddPolicyOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Plus className="h-4 w-4 mr-1" /> Agregar Política
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Nueva Política Personalizada</DialogTitle>
                            <DialogDescription>
                              Agrega una política ética de IA específica para tu organización
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <label className="text-sm font-medium">Categoría</label>
                              <Select value={newPolicyCategory} onValueChange={setNewPolicyCategory}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Transparencia">Transparencia</SelectItem>
                                  <SelectItem value="Accountability">Accountability</SelectItem>
                                  <SelectItem value="Equidad">Equidad</SelectItem>
                                  <SelectItem value="Gobernanza">Gobernanza</SelectItem>
                                  <SelectItem value="Gestión de Riesgos">Gestión de Riesgos</SelectItem>
                                  <SelectItem value="Privacidad y Datos">Privacidad y Datos</SelectItem>
                                  <SelectItem value="Seguridad">Seguridad</SelectItem>
                                  <SelectItem value="Ciclo de Vida">Ciclo de Vida</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-medium">Nombre de la Política</label>
                              <Input
                                placeholder="Ej: Auditoría de Datos de Entrenamiento"
                                value={newPolicyName}
                                onChange={(e) => setNewPolicyName(e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-medium">Descripción</label>
                              <Textarea
                                placeholder="Describe el propósito y alcance de esta política..."
                                value={newPolicyDescription}
                                onChange={(e) => setNewPolicyDescription(e.target.value)}
                                rows={3}
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-medium">Prioridad</label>
                              <Select value={newPolicyPriority} onValueChange={setNewPolicyPriority}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Alta">Alta</SelectItem>
                                  <SelectItem value="Media">Media</SelectItem>
                                  <SelectItem value="Baja">Baja</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setIsAddPolicyOpen(false)}>
                              Cancelar
                            </Button>
                            <Button onClick={handleAddPolicy} disabled={!newPolicyName.trim()}>
                              Agregar Política
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>

                  {/* Diálogo de recomendaciones IA */}
                  <Dialog open={isRecommendDialogOpen} onOpenChange={setIsRecommendDialogOpen}>
                    <DialogContent className="max-w-3xl max-h-[80vh]">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <Sparkles className="h-5 w-5 text-primary" />
                          Políticas Recomendadas por IA (ISO 42001)
                        </DialogTitle>
                        <DialogDescription>
                          Basadas en tu diagnóstico empresarial y configuración del CoE. Selecciona las que quieras agregar.
                        </DialogDescription>
                      </DialogHeader>
                      <ScrollArea className="max-h-[55vh] pr-4">
                        <div className="space-y-3">
                          {recommendedPolicies.map((policy, idx) => (
                            <div
                              key={idx}
                              className={`p-4 rounded-lg border transition-colors ${
                                policy.selected ? 'border-primary bg-primary/5' : 'border-border'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <Checkbox
                                  checked={policy.selected}
                                  onCheckedChange={(checked) => {
                                    setRecommendedPolicies(prev =>
                                      prev.map((p, i) => i === idx ? { ...p, selected: !!checked } : p)
                                    );
                                  }}
                                  className="mt-1"
                                />
                                <div className="flex-1 space-y-2">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-sm">{policy.name}</span>
                                    <Badge variant="secondary" className="text-xs">{policy.category}</Badge>
                                    <Badge
                                      variant={policy.priority === 'Alta' ? 'destructive' : 'secondary'}
                                      className="text-xs"
                                    >
                                      {policy.priority}
                                    </Badge>
                                    <Badge variant="outline" className="text-xs">{policy.iso_reference}</Badge>
                                  </div>
                                  <p className="text-sm text-muted-foreground">{policy.description}</p>
                                  <p className="text-xs text-muted-foreground italic">
                                    <strong>Justificación:</strong> {policy.justification}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                      <DialogFooter className="flex items-center justify-between">
                        <div className="text-sm text-muted-foreground">
                          {recommendedPolicies.filter(p => p.selected).length} de {recommendedPolicies.length} seleccionadas
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => setIsRecommendDialogOpen(false)}>
                            Cancelar
                          </Button>
                          <Button
                            onClick={handleAddSelectedPolicies}
                            disabled={recommendedPolicies.filter(p => p.selected).length === 0}
                          >
                            Agregar Seleccionadas
                          </Button>
                        </div>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Agrupar por categoría dinámicamente */}
                    {Array.from(new Set(policies.map(p => p.category))).sort().map((category) => {
                      const categoryPolicies = policies.filter(p => p.category === category);
                      const implementedCount = categoryPolicies.filter(p => p.status === 'implemented').length;
                      
                      return (
                        <div key={category} className="space-y-3">
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-lg">{category}</h3>
                            <Badge variant="secondary">
                              {implementedCount}/{categoryPolicies.length} implementadas
                            </Badge>
                          </div>
                          <div className="space-y-3 ml-4">
                            {categoryPolicies.map((policy) => (
                              <div key={policy.id} className="space-y-2">
                                <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium text-sm">{policy.name}</p>
                                      <Badge 
                                        variant={policy.priority === "Alta" ? "destructive" : "secondary"}
                                        className="text-xs"
                                      >
                                        {policy.priority}
                                      </Badge>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {policy.description}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      size="sm"
                                      variant={policy.status === "implemented" ? "default" : "outline"}
                                      onClick={() => toggleStatus({ id: policy.id!, currentStatus: policy.status })}
                                    >
                                      {policy.status === "implemented" ? (
                                        <>
                                          <CheckCircle2 className="h-4 w-4 mr-1" />
                                          Implementada
                                        </>
                                      ) : (
                                        "Marcar como implementada"
                                      )}
                                    </Button>
                                    {!policy.is_default && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => deletePolicy(policy.id!)}
                                        className="text-destructive hover:text-destructive"
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                <div className="ml-3">
                                  <Textarea
                                    placeholder="Notas de implementación (evidencia, responsables, timeline...)"
                                    defaultValue={policy.notes || ''}
                                    onBlur={(e) => {
                                      if (e.target.value !== policy.notes) {
                                        updatePolicyNotes({ id: policy.id!, notes: e.target.value });
                                      }
                                    }}
                                    className="text-xs min-h-[60px]"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Selecciona una organización específica para gestionar políticas
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Marco Regulatorio */}
        <TabsContent value="regulatory" className="space-y-6">
          {selectedOrg && selectedOrg !== 'all' ? (
            complianceLoading ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  Cargando marco regulatorio...
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Header con botones */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold">Marco Regulatorio</h2>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <Info className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-xs">
                            <p>Activa los marcos regulatorios aplicables a tu organización (EU AI Act, GDPR, ISO 42001, etc.). Para cada marco activo, marca el cumplimiento de sus requisitos y registra evidencias en las notas. Usa "Recomendar con IA" para identificar requisitos relevantes según tu sector y proyectos.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p className="text-sm text-muted-foreground">Gestiona el cumplimiento regulatorio de IA. Activa los marcos que apliquen a tu organización.</p>
                  </div>
                  <div className="flex gap-2">
                    <Dialog open={isAddReqOpen} onOpenChange={setIsAddReqOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Plus className="h-4 w-4 mr-1" />
                          Agregar Requisito
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Agregar Requisito Manual</DialogTitle>
                          <DialogDescription>Agrega un requisito personalizado a un marco regulatorio.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Marco Regulatorio</label>
                            <Select value={newReqFramework} onValueChange={setNewReqFramework}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {frameworks.map(f => (
                                  <SelectItem key={f.framework} value={f.framework}>{f.framework}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Nombre del Requisito</label>
                            <Input placeholder="Ej: Evaluación de conformidad sectorial" value={newReqLabel} onChange={(e) => setNewReqLabel(e.target.value)} />
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Descripción</label>
                            <Textarea placeholder="Describe el requisito..." value={newReqDescription} onChange={(e) => setNewReqDescription(e.target.value)} rows={3} />
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Prioridad</label>
                            <Select value={newReqPriority} onValueChange={setNewReqPriority}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Alta">Alta</SelectItem>
                                <SelectItem value="Media">Media</SelectItem>
                                <SelectItem value="Baja">Baja</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setIsAddReqOpen(false)}>Cancelar</Button>
                          <Button onClick={() => {
                            if (newReqLabel.trim()) {
                              addRequirement({ framework_name: newReqFramework, label: newReqLabel.trim(), description: newReqDescription.trim(), priority: newReqPriority });
                              setNewReqLabel(''); setNewReqDescription(''); setIsAddReqOpen(false);
                            }
                          }} disabled={!newReqLabel.trim()}>Agregar</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    <Button
                      size="sm"
                      onClick={async () => {
                        if (!selectedOrg || selectedOrg === 'all') return;
                        setIsRecommendingReqs(true);
                        try {
                          const { data, error } = await supabase.functions.invoke('recommend-regulatory-requirements', {
                            body: { organization_id: selectedOrg },
                          });
                          if (error) throw error;
                          if (data?.requirements) {
                            setRecommendedRequirements(data.requirements.map((r: any) => ({ ...r, selected: true })));
                            setIsRecommendReqsDialogOpen(true);
                          } else {
                            toast({ title: 'Sin recomendaciones', description: 'La IA no generó recomendaciones.', variant: 'destructive' });
                          }
                        } catch (err: any) {
                          toast({ title: 'Error', description: err.message || 'No se pudieron obtener recomendaciones.', variant: 'destructive' });
                        } finally {
                          setIsRecommendingReqs(false);
                        }
                      }}
                      disabled={isRecommendingReqs}
                    >
                      {isRecommendingReqs ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                      Recomendar con IA
                    </Button>
                  </div>
                </div>

                {/* Diálogo de recomendaciones IA de requisitos */}
                <Dialog open={isRecommendReqsDialogOpen} onOpenChange={setIsRecommendReqsDialogOpen}>
                  <DialogContent className="max-w-3xl max-h-[80vh]">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-primary" />
                        Requisitos Regulatorios Recomendados por IA
                      </DialogTitle>
                      <DialogDescription>
                        Basados en tu diagnóstico empresarial, proyectos y configuración del CoE. Selecciona los que quieras agregar.
                      </DialogDescription>
                    </DialogHeader>
                    <ScrollArea className="max-h-[55vh] pr-4">
                      <div className="space-y-3">
                        {recommendedRequirements.map((req, idx) => (
                          <div
                            key={idx}
                            className={`p-4 rounded-lg border transition-colors ${
                              req.selected ? 'border-primary bg-primary/5' : 'border-border'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <Checkbox
                                checked={req.selected}
                                onCheckedChange={(checked) => {
                                  setRecommendedRequirements(prev =>
                                    prev.map((r, i) => i === idx ? { ...r, selected: !!checked } : r)
                                  );
                                }}
                                className="mt-1"
                              />
                              <div className="flex-1 space-y-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold text-sm">{req.label}</span>
                                  <Badge variant="secondary" className="text-xs">{req.framework_name}</Badge>
                                  <Badge variant={req.priority === 'Alta' ? 'destructive' : 'secondary'} className="text-xs">{req.priority}</Badge>
                                  <Badge variant="outline" className="text-xs">{req.reference}</Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">{req.description}</p>
                                <p className="text-xs text-muted-foreground italic">
                                  <strong>Justificación:</strong> {req.justification}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                    <DialogFooter className="flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">
                        {recommendedRequirements.filter(r => r.selected).length} de {recommendedRequirements.length} seleccionados
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setIsRecommendReqsDialogOpen(false)}>Cancelar</Button>
                        <Button
                          onClick={() => {
                            const selected = recommendedRequirements.filter(r => r.selected);
                            selected.forEach(r => {
                              addRequirement({
                                framework_name: r.framework_name,
                                label: r.label,
                                description: `${r.description}\n\nRef: ${r.reference}\nJustificación: ${r.justification}`,
                                priority: r.priority,
                              });
                            });
                            setIsRecommendReqsDialogOpen(false);
                            setRecommendedRequirements([]);
                            toast({ title: `${selected.length} requisitos agregados`, description: 'Los requisitos recomendados se han agregado exitosamente.' });
                          }}
                          disabled={recommendedRequirements.filter(r => r.selected).length === 0}
                        >
                          Agregar Seleccionados
                        </Button>
                      </div>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Frameworks */}
                <div className="grid gap-6">
                  {frameworks.map((framework) => {
                    const frameworkCompliance = compliance.filter(c => c.framework_name === framework.framework);
                    const compliantCount = frameworkCompliance.filter(c => c.is_compliant).length;
                    const totalCount = frameworkCompliance.length;
                    const isActive = isFrameworkActive(framework.framework);
                    const frameworkSelection = frameworkSelections.find(s => s.framework_name === framework.framework);

                    return (
                      <Card key={framework.framework} className={!isActive ? 'opacity-60' : ''}>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Scale className="h-6 w-6 text-primary" />
                              <div>
                                <CardTitle className="flex items-center gap-2">
                                  {framework.framework}
                                  {!isActive && <Badge variant="secondary" className="text-xs">Desactivado</Badge>}
                                </CardTitle>
                                <CardDescription>
                                  {compliantCount}/{totalCount} requisitos cumplidos
                                </CardDescription>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Progress value={totalCount > 0 ? (compliantCount / totalCount) * 100 : 0} className="w-32" />
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div>
                                      <Checkbox
                                        checked={isActive}
                                        onCheckedChange={() => {
                                          if (frameworkSelection) {
                                            toggleFramework({ id: frameworkSelection.id!, isActive: frameworkSelection.is_active });
                                          }
                                        }}
                                      />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {isActive ? 'Desactivar este marco' : 'Activar este marco'}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </div>
                        </CardHeader>
                        {isActive && (
                          <CardContent>
                            <div className="space-y-3">
                              {frameworkCompliance.map((complianceRecord) => {
                                const hardcodedReq = framework.requirements.find(r => r.key === complianceRecord.requirement_key);
                                const displayLabel = complianceRecord.label || hardcodedReq?.label || complianceRecord.requirement_key;

                                return (
                                  <div key={complianceRecord.id} className="space-y-2">
                                    <div className="flex items-center gap-3 p-3 rounded-lg border">
                                      <Checkbox
                                        checked={complianceRecord.is_compliant || false}
                                        onCheckedChange={() => toggleCompliance({
                                          id: complianceRecord.id!,
                                          isCompliant: complianceRecord.is_compliant,
                                        })}
                                      />
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <p className="text-sm font-medium">{displayLabel}</p>
                                          {complianceRecord.priority && (
                                            <Badge variant={complianceRecord.priority === 'Alta' ? 'destructive' : 'secondary'} className="text-xs">
                                              {complianceRecord.priority}
                                            </Badge>
                                          )}
                                          {complianceRecord.is_custom && (
                                            <Badge variant="outline" className="text-xs">Custom</Badge>
                                          )}
                                        </div>
                                        {complianceRecord.description && (
                                          <p className="text-xs text-muted-foreground mt-1">{complianceRecord.description}</p>
                                        )}
                                      </div>
                                      {complianceRecord.is_custom && (
                                        <Button size="sm" variant="ghost" onClick={() => deleteRequirement(complianceRecord.id!)} className="text-destructive hover:text-destructive">
                                          <X className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </div>
                                    <div className="ml-10">
                                      <Textarea
                                        placeholder="Evidencia de cumplimiento (procesos, documentación, sistemas...)"
                                        defaultValue={complianceRecord.notes || ''}
                                        onBlur={(e) => {
                                          if (e.target.value !== complianceRecord.notes) {
                                            updateComplianceNotes({ id: complianceRecord.id!, notes: e.target.value });
                                          }
                                        }}
                                        className="text-xs min-h-[60px]"
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </>
            )
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Selecciona una organización específica para gestionar cumplimiento regulatorio
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Procesos */}
        <TabsContent value="processes" className="space-y-6">
          <div className="flex items-center gap-2 mb-2">
            <h2 className="text-lg font-semibold">Procesos del Centro de Excelencia</h2>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p>Define los procesos clave del CoE (gestión de modelos, gobernanza de datos, evaluación de proyectos, etc.) y evalúa su nivel de madurez con el slider (0=Inicial a 5=Optimizado). Usa "Recomendar con IA" para obtener procesos sugeridos basados en ISO 42001 y tu contexto empresarial.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        {selectedOrg && selectedOrg !== 'all' ? (
            processesLoading ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  Cargando procesos...
                </CardContent>
              </Card>
            ) : (
              <>
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Workflow className="h-5 w-5 text-primary" />
                          Procesos del Centro de Excelencia
                        </CardTitle>
                        <CardDescription>
                          Gestiona los procesos clave para la gestión de IA en la organización
                        </CardDescription>
                      </div>
                      <Button
                        variant="outline"
                        className="gap-2"
                        disabled={isRecommendingProcesses}
                        onClick={async () => {
                          setIsRecommendingProcesses(true);
                          try {
                            const { data, error } = await supabase.functions.invoke('recommend-coe-processes', {
                              body: { organization_id: selectedOrg },
                            });
                            if (error) throw error;
                            if (data?.processes) {
                              setRecommendedProcessesList(data.processes.map((p: any) => ({ ...p, selected: true })));
                              setIsRecommendProcessDialogOpen(true);
                            } else {
                              toast({ title: 'Sin recomendaciones', description: 'La IA no generó procesos.', variant: 'destructive' });
                            }
                          } catch (err: any) {
                            toast({ title: 'Error', description: err.message || 'Error al generar recomendaciones', variant: 'destructive' });
                          } finally {
                            setIsRecommendingProcesses(false);
                          }
                        }}
                      >
                        {isRecommendingProcesses ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                        Recomendar con IA
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex gap-2">
                        <Input
                          placeholder="Nombre del proceso"
                          value={newProcessName}
                          onChange={(e) => setNewProcessName(e.target.value)}
                        />
                        <Input
                          placeholder="Descripción"
                          value={newProcessDesc}
                          onChange={(e) => setNewProcessDesc(e.target.value)}
                        />
                        <Button 
                          onClick={() => {
                            if (newProcessName.trim()) {
                              addProcess({
                                name: newProcessName.trim(),
                                description: newProcessDesc.trim() || null,
                                maturity_level: 0,
                                sort_order: processes.length,
                                is_custom: true,
                              });
                              setNewProcessName('');
                              setNewProcessDesc('');
                            }
                          }}
                          disabled={!newProcessName.trim()}
                          className="shrink-0"
                        >
                          <Plus className="h-4 w-4 mr-1" /> Agregar
                        </Button>
                      </div>

                      {processes.length > 0 ? (
                        <div className="space-y-3">
                          {processes.map((process: any) => (
                            <Card key={process.id} className="p-4">
                              <div className="space-y-3">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="font-semibold text-sm">{process.name}</h4>
                                      {process.category && (
                                        <Badge variant="outline" className="text-xs">{process.category}</Badge>
                                      )}
                                      {process.priority && (
                                        <Badge variant={process.priority === 'Alta' ? 'destructive' : process.priority === 'Media' ? 'default' : 'secondary'} className="text-xs">
                                          {process.priority}
                                        </Badge>
                                      )}
                                    </div>
                                    {process.description && (
                                      <p className="text-xs text-muted-foreground mt-1">{process.description}</p>
                                    )}
                                    {process.reference && (
                                      <p className="text-xs text-primary mt-1">📋 {process.reference}</p>
                                    )}
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                                    onClick={() => deleteProcess(process.id!)}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                                
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between text-xs">
                                    <span className="text-muted-foreground">Nivel de madurez:</span>
                                    <span className="font-medium">{process.maturity_level}/5</span>
                                  </div>
                                  <Slider
                                    defaultValue={[process.maturity_level || 0]}
                                    onValueCommit={([value]) => updateMaturity({ id: process.id!, maturity_level: value })}
                                    max={5}
                                    step={1}
                                    className="w-full"
                                  />
                                  <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>Inicial</span>
                                    <span>Gestionado</span>
                                    <span>Definido</span>
                                    <span>Cuantificado</span>
                                    <span>Optimizado</span>
                                  </div>
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 space-y-4">
                          <p className="text-sm text-muted-foreground">
                            No hay procesos registrados. Agrega manualmente o usa el recomendador con IA.
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Diálogo de procesos recomendados por IA */}
                <Dialog open={isRecommendProcessDialogOpen} onOpenChange={setIsRecommendProcessDialogOpen}>
                  <DialogContent className="max-w-2xl max-h-[80vh]">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-primary" />
                        Procesos Recomendados por IA
                      </DialogTitle>
                      <DialogDescription>
                        Basado en ISO 42001, diagnóstico empresarial, políticas, riesgos y proyectos. Selecciona los procesos que deseas agregar.
                      </DialogDescription>
                    </DialogHeader>
                    <ScrollArea className="max-h-[55vh]">
                      <div className="space-y-3 pr-4">
                        {recommendedProcessesList.map((proc, idx) => (
                          <Card key={idx} className={`p-3 cursor-pointer transition-colors ${proc.selected ? 'border-primary bg-primary/5' : 'opacity-60'}`}
                            onClick={() => {
                              setRecommendedProcessesList(prev => prev.map((p, i) => i === idx ? { ...p, selected: !p.selected } : p));
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <Checkbox checked={proc.selected} className="mt-1" />
                              <div className="flex-1 space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-sm">{proc.name}</span>
                                  <Badge variant="outline" className="text-xs">{proc.category}</Badge>
                                  <Badge variant={proc.priority === 'Alta' ? 'destructive' : proc.priority === 'Media' ? 'default' : 'secondary'} className="text-xs">
                                    {proc.priority}
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground">{proc.description}</p>
                                <p className="text-xs text-primary">📋 {proc.reference}</p>
                                <p className="text-xs italic text-muted-foreground">💡 {proc.justification}</p>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </ScrollArea>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsRecommendProcessDialogOpen(false)}>Cancelar</Button>
                      <Button
                        disabled={!recommendedProcessesList.some(p => p.selected)}
                        onClick={() => {
                          const selected = recommendedProcessesList.filter(p => p.selected);
                          selected.forEach((proc, idx) => {
                            addProcess({
                              name: proc.name,
                              description: proc.description,
                              maturity_level: 0,
                              sort_order: processes.length + idx,
                              category: proc.category,
                              reference: proc.reference,
                              priority: proc.priority,
                              is_custom: false,
                            });
                          });
                          setIsRecommendProcessDialogOpen(false);
                          toast({ title: `${selected.length} procesos agregados`, description: 'Los procesos recomendados se han agregado al CoE.' });
                        }}
                      >
                        Agregar {recommendedProcessesList.filter(p => p.selected).length} Procesos
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </>
            )
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Selecciona una organización específica para gestionar procesos
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <CoERoleDialog
        open={roleDialogOpen}
        onOpenChange={setRoleDialogOpen}
        initial={editingRole}
        onSave={(data) => {
          if (editingRole?.id) {
            updateRole({ id: editingRole.id, updates: data });
          } else {
            addRole({ ...data, role_key: '' } as any);
          }
          setRoleDialogOpen(false);
          setEditingRole(null);
        }}
      />
    </div>
  );
}
